import { useState } from 'react';
import { track } from '@vercel/analytics';
import logger from '../utils/logger';
import { isJsonResponse, parseJsonResponse } from '../utils/feedbackParsers';
import { getCategoryGradeConfig } from '../utils/categoryGradeConfig';

/**
 * Custom hook for managing feedback editing operations
 * Handles add, edit, delete feedback functionality for both JSON and text-based feedback
 */
export function useFeedbackEditor(
  category,
  userData,
  taskId,
  authToken,
  API_BASE_URL,
  result,
  localJsonData,
  setLocalJsonData,
  parsedFeedback,
  setParsedFeedback,
  commentRatings,
  setCommentRatings
) {
  // State for editing feedback items
  const [editableFeedback, setEditableFeedback] = useState(null);
  const [isEditingFeedback, setIsEditingFeedback] = useState(false);
  const [updatedFeedbackContent, setUpdatedFeedbackContent] = useState('');
  const [feedbackUpdateStatus, setFeedbackUpdateStatus] = useState(null);
  
  // State for adding feedback
  const [isAddingFeedback, setIsAddingFeedback] = useState(false);
  const [addFeedbackParaIndex, setAddFeedbackParaIndex] = useState(null);
  const [newFeedbackContent, setNewFeedbackContent] = useState('');
  const [newFeedbackType, setNewFeedbackType] = useState('P');

  // Helper function to check if category is GCSE (enhanced to match main frontend)
  const isGCSECategory = (category) => {
    return category === 'GCSE_Shakespeare' || 
           category === 'GCSE_19th' || 
           category === 'GCSE_Poetry' || 
           category === 'GCSE_Inspector';
  };

  // Helper function to check if category is GCSE Language (enhanced to match main frontend)
  const isGCSELanguageCategory = (category) => {
    return category === 'GCSE_French_Q1' || 
           category === 'GCSE_French_Q2' || 
           category === 'GCSE_Spanish_Q1' || 
           category === 'GCSE_Spanish_Q2' || 
           category === 'GCSE_German_Q1' || 
           category === 'GCSE_German_Q2' ||
           category === 'GCSE_FR_Grammar' || 
           category === 'GCSE_ES_Grammar' || 
           category === 'GCSE_DE_Grammar';
  };

  // Get appropriate feedback type options based on category (enhanced to match main frontend)
  const getFeedbackTypeOptions = () => {
    console.log('getFeedbackTypeOptions called with category:', category);
    
    // If category is not loaded yet, return default
    if (!category || category === '') {
      console.log('Category not loaded yet, returning default PCLM');
      return [
        { value: 'P', label: 'Purpose (P)', color: '#2ecc71' },
        { value: 'C', label: 'Coherence (C)', color: '#e74c3c' },
        { value: 'L', label: 'Language (L)', color: '#3498db' },
        { value: 'M', label: 'Mechanics (M)', color: '#f1c40f' }
      ];
    }
    
    // First prefer centralized config (preserve AO codes for GCSE categories with config)
    try {
      const config = getCategoryGradeConfig(category);
      if (config && Array.isArray(config.gradeCategories) && config.gradeCategories.length > 0) {
        return config.gradeCategories.map(gc => ({
          value: gc.code,
          label: `${gc.name} (${gc.code})`,
          color: gc.color
        }));
      }
    } catch (e) {
      logger.warn('Could not load category grade config, falling back to legacy options');
    }
    
    if (isGCSECategory(category)) {
      return [
        { value: 'A', label: 'Argument (A)', color: '#9b59b6' },
        { value: 'L', label: 'Language (L)', color: '#3498db' },
        { value: 'C', label: 'Context (C)', color: '#f39c12' },
        { value: 'M', label: 'Mechanics (M)', color: '#e74c3c' }
      ];
    } else if (category === 'VCE_English_SectionA' || category === 'VCE_English_SectionC') {
      return [
        { value: 'K', label: 'Knowledge (K)', color: '#27ae60' },
        { value: 'C', label: 'Coherence (C)', color: '#9b59b6' },
        { value: 'E', label: 'Evidence (E)', color: '#e74c3c' },
        { value: 'L', label: 'Language (L)', color: '#3498db' }
      ];
    } else if (category === 'VCE_English_SectionB') {
      return [
        { value: 'E', label: 'Engagement (E)', color: '#e67e22' },
        { value: 'P', label: 'Purpose (P)', color: '#2ecc71' },
        { value: 'S', label: 'Structure (S)', color: '#9b59b6' },
        { value: 'L', label: 'Language (L)', color: '#3498db' }
      ];
    } else if (category === 'Australian_HSC' || category === 'Australian_Textual' || category === 'Australian_Critical' ||
               category === 'English_HSC_Textual' || category === 'English_HSC_Critical' || category === 'English_HSC_Texts' || 
               category === 'English_HSC_Craft' || category === 'English_HSC_CraftA' || category === 'English_HSC_CraftB') {
      return [
        { value: 'E', label: 'Engagement (E)', color: '#e67e22' },
        { value: 'S', label: 'Structure (S)', color: '#9b59b6' },
        { value: 'A', label: 'Analysis (A)', color: '#e74c3c' },
        { value: 'L', label: 'Language (L)', color: '#3498db' }
      ];
    } else if (category === 'Aiste' || category === 'Grammar Checker' || category === 'IE_Oral') {
      return [
        { value: 'G', label: 'Gaeilge (G)', color: '#16a085' },
        { value: 'C', label: 'Content (C)', color: '#c0392b' },
        { value: 'S', label: 'Style (S)', color: '#8e44ad' }
      ];
    } else if (category.startsWith('Filiocht_')) {
      return [
        { value: 'G', label: 'Gaeilge (G)', color: '#16a085' },
        { value: 'C', label: 'Content (C)', color: '#c0392b' }
      ];
    } else if (category.startsWith('FR_') || category.startsWith('ES_')) {
      return [
        { value: 'C', label: 'Communication (C)', color: '#FF6B6B' },
        { value: 'L', label: 'Language (L)', color: '#4ECDC4' }
      ];
    } else if (category.startsWith('DE_')) {
      return [
        { value: 'C', label: 'Content (C)', color: '#2ecc71' },
        { value: 'E', label: 'Expression (E)', color: '#3498db' }
      ];
    } else if (isGCSELanguageCategory(category)) {
      return [
        { value: 'C', label: 'Content (C)', color: '#f39c12' },
        { value: 'L', label: 'Language (L)', color: '#3498db' },
        { value: 'A', label: 'Accuracy (A)', color: '#e74c3c' }
      ];
    } else {
      // Default English Leaving Cert (PCLM)
      return [
        { value: 'P', label: 'Purpose (P)', color: '#2ecc71' },
        { value: 'C', label: 'Coherence (C)', color: '#e74c3c' },
        { value: 'L', label: 'Language (L)', color: '#3498db' },
        { value: 'M', label: 'Mechanics (M)', color: '#f1c40f' }
      ];
    }
  };

  // Handle editing a feedback item
  const handleEditFeedback = (paraIndex, fbIndex, feedbackContent) => {
    // Only allow teachers to edit feedback
    if (userData?.role !== 'teacher') return;
    
    track('feedback_edit_started', { 
      category: category,
      isTeacher: true
    });
    
    // Store more detailed information to help with recovery
    const editInfo = { 
      paraIndex, 
      fbIndex,
      content: feedbackContent,
      contentKey: feedbackContent.substring(0, 50),
      // Extract the feedback type (P, C, L, M, G, S, E)
      type: (feedbackContent.match(/^\{([PACLMGSE]):/) || [null, 'P'])[1]
    };
    
    setEditableFeedback(editInfo);
    setIsEditingFeedback(true);
    
    // Clean the feedback content for editing (remove any brackets and TEACHER tag)
    let cleanedContent = feedbackContent
      .replace(/^\{[^:]+:\s*/, '') // Remove opening tag (any content before colon)
      .replace(/\}(\s*\[TEACHER\])?$/, '') // Remove closing bracket and [TEACHER] tag
      .trim();
      
    // Additional cleaning: remove [TEACHER] tag if it appears anywhere in the content
    // Make this more robust to handle various formats
    cleanedContent = cleanedContent.replace(/\s*\[TEACHER\]\s*/g, '').trim();
    cleanedContent = cleanedContent.replace(/\[TEACHER\]/g, '').trim(); // Also handle without spaces

    // Decode Unicode escape sequences for editing
    try {
      cleanedContent = cleanedContent.replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
        return String.fromCharCode(parseInt(code, 16));
      });
    } catch (e) {
      logger.error('Failed to decode Unicode in feedback content for editing:', e);
    }
      
    logger.info('Preparing feedback for editing', {
      original: feedbackContent,
      cleaned: cleanedContent,
      editInfo
    });
    
    // Store this information in localStorage as a backup
    try {
      localStorage.setItem('currentEditingFeedback', JSON.stringify(editInfo));
    } catch (e) {
      logger.error('Failed to store editing info in localStorage:', e);
    }
    
    setUpdatedFeedbackContent(cleanedContent);
  };

  // Handle deleting a feedback item
  const handleDeleteFeedback = async (paraIndex, fbIndex) => {
    // Only allow teachers to delete feedback
    if (userData?.role !== 'teacher' || !taskId) return;
    
    if (window.confirm('Are you sure you want to delete this feedback item?')) {
      try {
        setFeedbackUpdateStatus('saving');
        
        track('feedback_deleted', { 
          category: category,
          isTeacher: true,
          feedbackIndex: `${paraIndex}-${fbIndex}`
        });
        
        // Log starting state for debugging
        logger.debug(`BEFORE DELETION - Current ratings:`, JSON.stringify(commentRatings));
        logger.debug(`Deleting comment at paragraph ${paraIndex}, feedback index ${fbIndex}`);
        
        // Debug auth token
        logger.debug('Auth token for delete operation:', {
          authTokenExists: !!authToken,
          authTokenType: typeof authToken,
          authTokenLength: authToken?.length || 0,
          authTokenPrefix: authToken?.substring(0, 20) + '...' || 'NO_TOKEN',
          taskId: taskId,
          apiBaseUrl: API_BASE_URL
        });
        
        // Determine if this is JSON-based or text-based feedback
        const isJson = isJsonResponse(result);
        const jsonData = isJson ? parseJsonResponse(result) : null;
        
        let feedbackContent = null;
        
        if (isJson && jsonData) {
          // For JSON feedback, get content from jsonData.paragraphs
          const paragraphs = jsonData.paragraphs || [];
          if (paraIndex < paragraphs.length && fbIndex < paragraphs[paraIndex].feedback.length) {
            const feedbackItem = paragraphs[paraIndex].feedback[fbIndex];
            feedbackContent = `{${feedbackItem.type}: ${feedbackItem.content}}`;
          }
        } else {
          // For text-based feedback, get content from parsedFeedback
          if (parsedFeedback[paraIndex] && parsedFeedback[paraIndex].feedback[fbIndex]) {
            feedbackContent = parsedFeedback[paraIndex].feedback[fbIndex];
          }
        }
        
        if (!feedbackContent) {
          throw new Error('Feedback item not found');
        }
        
        // Call API to update the feedback in the database
        const response = await fetch(`${API_BASE_URL}/api/results/${taskId}/update_feedback`, {
          method: 'POST',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            operation: 'delete',
            feedbackKey: `${paraIndex}-${fbIndex}`,
            feedbackContent: feedbackContent
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            errorData = { error: errorText };
          }
          logger.error('Error response from delete feedback API', {
            status: response.status,
            errorData
          });
          throw new Error(errorData.error || 'Failed to delete feedback');
        }

        if (isJson && jsonData) {
          // For JSON feedback: Update the local JSON data immediately for UI feedback
          const updatedJsonData = JSON.parse(JSON.stringify(localJsonData || jsonData));
          const paragraphs = updatedJsonData.paragraphs || [];
          
          if (paraIndex < paragraphs.length && fbIndex < paragraphs[paraIndex].feedback.length) {
            // Remove the feedback item from the local data
            paragraphs[paraIndex].feedback.splice(fbIndex, 1);
            
            // Update the local state to immediately reflect the change
            setLocalJsonData(updatedJsonData);
            
            logger.info('JSON feedback deleted and UI updated immediately');
          }
        } else {
          // For text-based feedback: Update the parsedFeedback state
          const newParsedFeedback = JSON.parse(JSON.stringify(parsedFeedback));
          newParsedFeedback[paraIndex].feedback.splice(fbIndex, 1);
          setParsedFeedback(newParsedFeedback);
          
          // Handle rating remapping for text-based feedback
          const originalFeedback = [...parsedFeedback[paraIndex].feedback];
          
          // Create content-to-rating mapping
          const feedbackContentToRating = {};
          let hasRatings = false;
          
          originalFeedback.forEach((content, idx) => {
            const ratingKey = `${paraIndex}-${idx}`;
            if (commentRatings[ratingKey] && content) {
              hasRatings = true;
              // Handle both string and object feedback formats
              const contentStr = typeof content === 'string' ? content : 
                                typeof content === 'object' && content.content ? content.content : 
                                JSON.stringify(content);
              const contentKey = contentStr.substring(0, 50);
              feedbackContentToRating[contentKey] = commentRatings[ratingKey];
            }
          });
          
          if (hasRatings) {
            // Create new ratings object
            const newRatings = {};
            Object.entries(commentRatings).forEach(([key, value]) => {
              if (!key.startsWith(`${paraIndex}-`)) {
                newRatings[key] = value;
              }
            });
            
            // Map new feedback to original ratings based on content
            const newFeedback = newParsedFeedback[paraIndex].feedback;
            newFeedback.forEach((content, newIdx) => {
              // Handle both string and object feedback formats
              const contentStr = typeof content === 'string' ? content : 
                                typeof content === 'object' && content.content ? content.content : 
                                JSON.stringify(content);
              const contentKey = contentStr.substring(0, 50);
              if (feedbackContentToRating[contentKey]) {
                newRatings[`${paraIndex}-${newIdx}`] = feedbackContentToRating[contentKey];
              }
            });
            
            setCommentRatings(newRatings);
            
            // Save updated ratings to server
            try {
              const ratingsResponse = await fetch(
                `${API_BASE_URL}/api/results/${taskId}/update_ratings`,
                {
                  method: 'POST',
                  headers: {
                    'Authorization': authToken,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    commentRatings: newRatings,
                    replace_all: true
                  })
                }
              );
              
              if (!ratingsResponse.ok) {
                logger.error('Failed to update ratings after comment deletion');
              }
            } catch (ratingError) {
              logger.error('Error updating ratings after deletion:', ratingError);
            }
          }
        }
                
        setFeedbackUpdateStatus('success');
        setTimeout(() => setFeedbackUpdateStatus(null), 2000);
      } catch (error) {
        logger.error('Error deleting feedback:', error);
        setFeedbackUpdateStatus('error');
        alert(`Error deleting feedback: ${error.message}`);
      }
    }
  };

  // Handle saving the edited feedback
  const handleSaveEditedFeedback = async () => {
    if (!editableFeedback || userData?.role !== 'teacher' || !taskId) return;
    
    try {
      setFeedbackUpdateStatus('saving');
      
      const { paraIndex, fbIndex } = editableFeedback;
      
      // Determine if this is JSON-based or text-based feedback
      const isJson = isJsonResponse(result);
      const jsonData = isJson ? (localJsonData || parseJsonResponse(result)) : null;
      
      let originalFeedback = null;
      let feedbackCode = 'P'; // Default
      
      if (isJson && jsonData) {
        // For JSON feedback, get content from jsonData.paragraphs
        const paragraphs = jsonData.paragraphs || [];
        if (paraIndex < paragraphs.length && fbIndex < paragraphs[paraIndex].feedback.length) {
          const feedbackItem = paragraphs[paraIndex].feedback[fbIndex];
          originalFeedback = `{${feedbackItem.type}: ${feedbackItem.content}}`;
          feedbackCode = feedbackItem.type;
        }
      } else {
        // For text-based feedback, get from parsedFeedback
        if (parsedFeedback[paraIndex] && parsedFeedback[paraIndex].feedback[fbIndex]) {
          originalFeedback = parsedFeedback[paraIndex].feedback[fbIndex];
          // Updated regex to handle both single letters and AO codes
          const codeMatch = originalFeedback.match(/^\{([PACLMGSE]|AO\d+):/);
          feedbackCode = codeMatch ? codeMatch[1] : 'P';
        }
      }
      
      if (!originalFeedback) {
        throw new Error('Unable to find original feedback item');
      }
      
      // Format the new feedback with the same code and mark as teacher-edited
      let feedbackToSave = updatedFeedbackContent.trim();

      // Remove any existing [TEACHER] tags first to prevent duplicates
      feedbackToSave = feedbackToSave.replace(/\s*\[TEACHER\]\s*/g, '').trim();

      // Always add exactly one [TEACHER] tag at the end
      feedbackToSave += ' [TEACHER]';  // Add space before [TEACHER]
      
      const formattedFeedback = `{${feedbackCode}: ${feedbackToSave}}`;
      
      track('feedback_edited', { 
        category: category,
        isTeacher: true,
        feedbackIndex: `${paraIndex}-${fbIndex}`,
        feedbackType: feedbackCode
      });
      
      // Call API to update the feedback in the database
      const response = await fetch(`${API_BASE_URL}/api/results/${taskId}/update_feedback`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          operation: 'update',
          feedbackKey: `${paraIndex}-${fbIndex}`,
          content: formattedFeedback,
          feedbackContent: originalFeedback
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || 'Failed to save feedback');
      }
      
      // Update the UI based on feedback type
      if (isJson && jsonData) {
        // For JSON feedback: Update localJsonData immediately
        const updatedJsonData = JSON.parse(JSON.stringify(localJsonData || jsonData));
        const paragraphs = updatedJsonData.paragraphs || [];
        
        if (paraIndex < paragraphs.length && fbIndex < paragraphs[paraIndex].feedback.length) {
          paragraphs[paraIndex].feedback[fbIndex] = {
            type: feedbackCode,
            content: feedbackToSave // Keep the TEACHER tag in local content for immediate display
          };
          
          setLocalJsonData(updatedJsonData);
          logger.info('JSON feedback edited and UI updated immediately');
        }
      } else {
        // For text-based feedback: Update parsedFeedback
        const newParsedFeedback = JSON.parse(JSON.stringify(parsedFeedback));
        newParsedFeedback[paraIndex].feedback[fbIndex] = formattedFeedback;
        setParsedFeedback(newParsedFeedback);
      }
      
      // Reset the editing state
      setIsEditingFeedback(false);
      setEditableFeedback(null);
      setUpdatedFeedbackContent('');
      setFeedbackUpdateStatus('success');
      setTimeout(() => setFeedbackUpdateStatus(null), 2000);
    } catch (error) {
      logger.error('Error saving edited feedback:', error);
      setFeedbackUpdateStatus('error');
      alert(`Error saving feedback: ${error.message}`);
    }
  };

  // Handle canceling the feedback edit
  const handleCancelEdit = () => {
    track('feedback_edit_cancelled', { 
      category: category,
      isTeacher: true
    });
    
    setIsEditingFeedback(false);
    setEditableFeedback(null);
    setUpdatedFeedbackContent('');
  };

  // Handle adding a new feedback item
  const handleAddFeedback = (paraIndex) => {
    track('feedback_add_started', { 
      category: category,
      isTeacher: true,
      paragraphIndex: paraIndex
    });
    
    setIsAddingFeedback(true);
    setAddFeedbackParaIndex(paraIndex);
    setNewFeedbackContent('');
    
    // Set default feedback type based on category, preferring configured scheme (e.g., AO codes for GCSE)
    try {
      const config = getCategoryGradeConfig(category);
      if (config && Array.isArray(config.gradeCategories) && config.gradeCategories.length > 0) {
        setNewFeedbackType(config.gradeCategories[0].code);
        return;
      }
    } catch (e) {
      logger.warn('Could not load category grade config when setting default type; using legacy default');
    }
    
    if (isGCSECategory(category)) {
      setNewFeedbackType('A'); // Argument (legacy GCSE fallback)
    } else if (category === 'Aiste' || category === 'Grammar Checker' || category.startsWith('Filiocht_') || category === 'IE_Oral') {
      setNewFeedbackType('G'); // Gaeilge
    } else if (category.startsWith('FR_') || category.startsWith('ES_')) {
      setNewFeedbackType('C'); // Communication
    } else if (category.startsWith('DE_')) {
      setNewFeedbackType('C'); // Content
    } else {
      setNewFeedbackType('P'); // Purpose
    }
  };

  // Handle saving the new feedback
  const handleSaveNewFeedback = async () => {
    if (addFeedbackParaIndex === null || !newFeedbackContent.trim() || userData?.role !== 'teacher' || !taskId) {
      return;
    }
    
    try {
      setFeedbackUpdateStatus('saving');
      
      // Format the new feedback with the selected type and mark as teacher-added
      const formattedFeedback = `{${newFeedbackType}: ${newFeedbackContent.trim()}} [TEACHER]`;
      
      track('feedback_added', { 
        category: category,
        isTeacher: true,
        paragraphIndex: addFeedbackParaIndex,
        feedbackType: newFeedbackType,
        characterCount: newFeedbackContent.trim().length
      });
      
      // Check if this is JSON-based feedback
      const isJson = isJsonResponse(result);
      let newFbIndex;
      
      if (isJson) {
        // For JSON feedback, calculate the new index based on the JSON data
        const jsonData = localJsonData || parseJsonResponse(result);
        if (jsonData && jsonData.paragraphs && jsonData.paragraphs[addFeedbackParaIndex]) {
          newFbIndex = jsonData.paragraphs[addFeedbackParaIndex].feedback.length;
        } else {
          newFbIndex = 0;
        }
      } else {
        // For text-based feedback, use parsedFeedback
        newFbIndex = parsedFeedback[addFeedbackParaIndex]?.feedback?.length || 0;
      }
      
      const requestBody = {
        operation: 'add',
        feedbackKey: `${addFeedbackParaIndex}-${newFbIndex}`,
        paraIndex: addFeedbackParaIndex,
        content: formattedFeedback,
        type: newFeedbackType  // Add the type field for the backend
      };
      
      // Call API to add the new feedback
      const response = await fetch(`${API_BASE_URL}/api/results/${taskId}/update_feedback`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || `Failed to add feedback: ${response.status} ${response.statusText}`);
      }
      
      // Update the UI with the new feedback
      if (isJson) {
        // For JSON feedback, update the local JSON data
        if (localJsonData && localJsonData.paragraphs && localJsonData.paragraphs[addFeedbackParaIndex]) {
          const updatedJsonData = JSON.parse(JSON.stringify(localJsonData));
          updatedJsonData.paragraphs[addFeedbackParaIndex].feedback.push({
            type: newFeedbackType,
            content: `${newFeedbackContent.trim()} [TEACHER]`
          });
          setLocalJsonData(updatedJsonData);
        }
      } else {
        // For text-based feedback, update parsedFeedback
        const newParsedFeedback = JSON.parse(JSON.stringify(parsedFeedback));
        newParsedFeedback[addFeedbackParaIndex].feedback.push(formattedFeedback);
        setParsedFeedback(newParsedFeedback);
      }
      
      // Reset the adding state
      setIsAddingFeedback(false);
      setAddFeedbackParaIndex(null);
      setNewFeedbackContent('');
      setFeedbackUpdateStatus('success');
      setTimeout(() => setFeedbackUpdateStatus(null), 2000);
    } catch (error) {
      logger.error('Error adding new feedback:', error);
      setFeedbackUpdateStatus('error');
      alert(`Error adding feedback: ${error.message}`);
    }
  };

  // Handle canceling adding feedback
  const handleCancelAddFeedback = () => {
    track('feedback_add_cancelled', { 
      category: category,
      isTeacher: true
    });
    
    setIsAddingFeedback(false);
    setAddFeedbackParaIndex(null);
    setNewFeedbackContent('');
  };

  return {
    // State
    editableFeedback,
    isEditingFeedback,
    updatedFeedbackContent,
    setUpdatedFeedbackContent,
    feedbackUpdateStatus,
    isAddingFeedback,
    addFeedbackParaIndex,
    newFeedbackContent,
    setNewFeedbackContent,
    newFeedbackType,
    setNewFeedbackType,
    
    // Functions
    handleEditFeedback,
    handleDeleteFeedback,
    handleSaveEditedFeedback,
    handleCancelEdit,
    handleAddFeedback,
    handleSaveNewFeedback,
    handleCancelAddFeedback,
    getFeedbackTypeOptions
  };
} 